import {
  getGetFrontRichiesteIdQueryOptions,
  getGetFrontRichiesteQueryOptions,
  usePostFrontRichiesteIdAzioniAnnulla,
} from '@framework/api-client/api/inquiries-compile/richieste-controller/richieste-controller.ts';
import { SubmissionLogic } from '@framework/frontoffice-submission/submission/SubmissionLogic.ts';
import {
  ConfirmButton,
  type ConfirmButtonProps,
} from '@framework/ui/components/button/ConfirmButton.tsx';
import { getNetworkErrorMessage } from '@framework/ui/core/errors/getNetworkErrorMessage.tsx';
import { message } from 'antd';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

export interface ISubmissionUndoButtonProps extends ConfirmButtonProps {
  submissionId?: string | null;
}

/**
 * Pulsante per annullare una richiesta lato FO
 */
export function SubmissionUndoButton(props: ISubmissionUndoButtonProps) {
  const { submissionId, ...otherProps } = props;
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const undoMutation = usePostFrontRichiesteIdAzioniAnnulla({
    mutation: {
      onSuccess: data => {
        // biome-ignore format: <tbd>
        queryClient.invalidateQueries(getGetFrontRichiesteQueryOptions());
        // biome-ignore format: <tbd>
        queryClient.invalidateQueries(getGetFrontRichiesteIdQueryOptions(submissionId!));

        // Se il tipo domanda è gestito dal workflow, l'annullamento non è
        // immediato: la richiesta viene congelata in attesa dell'esito
        if (SubmissionLogic.isSubmissionUndoInProgress(data)) {
          message.info(
            t(
              'submission-undo-button.annullamento-inoltrato',
              "Richiesta di annullamento inoltrata. La richiesta resterà sospesa fino alla conferma dell'annullamento.",
            ),
          );
          return;
        }

        message.success(
          t(
            'submission-undo-button.richiesta-annullata',
            'Richiesta annullata con successo',
          ),
        );
      },
      onError: error => {
        // Il BE risponde 409 quando la richiesta non è annullabile
        // (procedimento concluso) o è già congelata da un annullamento:
        // in quel caso mostriamo direttamente il messaggio dedicato
        const errorMessage =
          axios.isAxiosError(error) && error.response?.status === 409
            ? t(
                'submission-undo-button.richiesta-non-annullabile',
                'La richiesta non è annullabile: il procedimento è concluso o è già in corso un annullamento.',
              )
            : getNetworkErrorMessage(
                error,
                t(
                  'submission-undo-button.errore-annullamento',
                  "Errore nell'annullamento della richiesta",
                ),
              );
        message.error(errorMessage);
      },
    },
  });

  return (
    <ConfirmButton
      {...otherProps}
      title={t(
        'submission-undo-button.conferma-annullamento',
        'Sei sicuro di voler annullare la richiesta?',
      )}
      buttonDanger={true}
      onConfirm={() => undoMutation.mutate({ id: submissionId! })}
      loading={undoMutation.isLoading}
      okText={t('submission-undo-button.annulla', 'Annulla')}
      cancelText={t('submission-undo-button.chiudi', 'Chiudi')}
    >
      {t('submission-undo-button.annulla-la-richiesta', 'Annulla la Richiesta')}
    </ConfirmButton>
  );
}
